import {ACCEPTED_IMAGE_FILE_TYPES, MAX_TASK_IMAGES, useMarkdownImageUpload} from '../../hooks/use-markdown-image-upload'
import {useClickFileSelect} from '@github-ui/use-unified-file-select'
import {ImageIcon} from '@primer/octicons-react'
import {IconButton, Tooltip} from '@primer/react'
import type {ComponentProps, RefObject} from 'react'

export type ImageAttachButtonProps = {
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  repositoryId?: string
  disabled?: boolean
  isAtImageLimit?: boolean
  size?: ComponentProps<typeof IconButton>['size']
}

/**
 * An image icon button that opens a file picker for attaching images to a task.
 */
export function ImageAttachButton({
  textAreaRef,
  repositoryId,
  disabled,
  isAtImageLimit,
  size = 'small',
}: ImageAttachButtonProps) {
  const {processFiles, isUploading} = useMarkdownImageUpload({
    textAreaRef,
    repositoryId,
  })

  const clickTargetProps = useClickFileSelect({
    onSelect: (accepted: File[]) => processFiles(accepted),
    multi: true,
    acceptedFileTypes: ACCEPTED_IMAGE_FILE_TYPES,
  })

  const isInactive = disabled || isUploading || isAtImageLimit

  const button = (
    <IconButton
      icon={ImageIcon}
      aria-label={isAtImageLimit ? `Image limit reached (${MAX_TASK_IMAGES})` : 'Attach images'}
      variant="invisible"
      size={size}
      inactive={isInactive}
      aria-disabled={isInactive}
      {...(!isInactive ? clickTargetProps : {})}
    />
  )

  if (isAtImageLimit) {
    return (
      <Tooltip text={`You can attach up to ${MAX_TASK_IMAGES} images per task`} direction="n">
        {button}
      </Tooltip>
    )
  }

  return button
}
